import { useState, useEffect, useCallback, KeyboardEventHandler } from 'react';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { IState } from './IState';

const useEditSubuserRights = (onClose: ICloseEvent['onClose']) => {
    const { subusers, devices, lang } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        firstLoad: true,
        rights: {
            send_commands: false,
            change_settings: false,
        },
    };
    const [state, setState] = useState(initState);

    const handleInputClick = useCallback(() => {
        subusers.resetServerError();
    }, []);
    const handleSendCommandsChange = useCallback(() => {
        setState((prev) => ({
            ...prev,
            rights: {
                ...prev.rights,
                send_commands: !prev.rights.send_commands,
            },
        }));
        handleInputClick();
    }, []);
    const handleSettingsChange = useCallback(() => {
        setState((prev) => ({
            ...prev,
            rights: {
                ...prev.rights,
                change_settings: !prev.rights.change_settings,
            },
        }));
        handleInputClick();
    }, []);
    const handleSaveClick = useCallback(() => {
        subusers.resetServerError();
        const { rights } = state;
        subusers.changeRights(rights);
    }, [state.rights]);
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleSaveClick();
                break;
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                const target = e.target as HTMLElement;
                target.click();
            }
        },
        [],
    );
    const handlers = {
        handleSaveClick,
        handleSendCommandsChange,
        handleSettingsChange,
        handleBtnKeyDown,
    };

    useEffect(() => {
        subusers.resetServerError();
        subusers.getRights();
        return () => {
            subusers.resetSelectedDeviceRights();
        };
    }, []);
    useEffect(() => {
        if (subusers.operationSuccess) {
            onClose();
            subusers.resetOperationSuccess();
        }
    }, [subusers.operationSuccess]);
    useEffect(() => {
        handleKey(key);
    }, [key]);
    useEffect(() => {
        const rights = subusers.selectedDeviceRights;
        if (rights && state.firstLoad) {
            setState((prev) => ({
                ...prev,
                rights,
                firstLoad: false,
            }));
        }
    }, [subusers.selectedDeviceRights]);
    useEffect(() => {
        if (!subusers.selectedSubuser && !subusers.loading) {
            onClose();
        }
    }, [subusers.selectedSubuser, subusers.loading]);
    useEffect(() => {
        const [subuser] = subusers.subusersDevices.filter(
            ({ subuser }) => subuser === subusers.selectedSubuser,
        );
        if (
            subuser &&
            subusers.selectedDevice &&
            !subuser.devices.includes(subusers.selectedDevice) &&
            !subusers.loading
        ) {
            onClose();
        }
    }, [subusers.subusersDevices, subusers.loading]);
    useEffect(() => {
        if (!subusers.selectedSubuser && !subusers.loading) {
            onClose();
        }
    }, [subusers.selectedSubuser, subusers.loading]);

    return {
        subusers,
        devices,
        lang,
        state,
        handlers,
    };
};

export default useEditSubuserRights;
